// ============================================================================
// StartDateMoveFields — the ONE form behind the database's question.
//
// `update_account_start_date()` refuses to move the planned start date of a
// building that has not opened unless somebody says why. That refusal is
// correct and it is not changing (TCSD-0243). What was missing was a box to
// type the answer into on every screen that offers the move.
//
// Until 2026-09-21 there were two callers and only one of them could satisfy
// the guard: the overdue resolution dialog had a reason field, and the Account
// start panel sent a bare date and always failed. Ben and Spencer both hit the
// refusal on Georgetown Surgery Center and neither could comply, because the
// screen they were on had nothing to type into.
//
// So the fields, the readiness rule, the payload and the call live here, once.
// A caller supplies the building, the current planned date, its own words and
// its own Cancel/Save chrome. ⛔ A CALLER NEVER ASSEMBLES THE PAYLOAD — that is
// how the second, broken copy came to exist in the first place.
//
// ⛔ EVERY EXPORT IS DECLARED AT MODULE SCOPE. A component declared inside
// another component is a new type on every render, so React remounts the
// subtree and every text field loses focus after one character (TCSD-0232).
// There is no lint in this repo to catch it. This file is a form; that is the
// exact failure an extraction produces, so it is spelled out here.
// ============================================================================

import { useCallback, useMemo, useState } from 'react';
import { useToast } from '../../shared/components';
import { errorMessage } from '../../shared/errors';
import { updateAccountStartDate } from './hooks/use-sop-data';

/** What the person has typed. Kept flat so the readiness rule and the payload
 *  can both be pure functions of it and tested without rendering anything. */
export interface StartDateMoveDraft {
  /** The new planned start date, YYYY-MM-DD. Ignored when `dateUnknown`. */
  date: string;
  /** "We don't know the new date yet" — a real answer, not an escape hatch. */
  dateUnknown: boolean;
  /** Why it is not starting when it was due to. */
  reason: string;
}

/**
 * The words. ⛔ ONE SET, AND THERE IS NO SECOND.
 *
 * Ben, 2026-09-21, when this run proposed a separate set for a building whose
 * date had already passed: *"The only reason you would move the planned start
 * date AFTER IT HAS PASSED is because you forgot to change it, and the
 * REALITY/TRUTH is that it did not start... Tell me why we need a second set
 * of categories."*
 *
 * He is right, and the measurement says so. The daily ask already runs on every
 * building that is not open whose date is today or earlier, it fires again the
 * next day, and it sends the account manager HERE — to the building's own page,
 * not to the overdue strip on the board, which held zero cards. So there is one
 * situation, not two: somebody is answering "did it open?" and saying "no, the
 * new date is this, because that." These words are written for that question,
 * and they read the same whether the date is three weeks out or nine days gone.
 *
 * ⛔ Do not add an on-time variant and a late variant. The only branch below is
 * on the ANSWER given — a known new date or not — which is a real difference in
 * what is being said, not a category of building.
 */
export const START_DATE_MOVE_COPY = {
  dateLabel: 'New planned start date',
  unknownLabel: 'We don’t know the new date yet',
  reasonLabel: 'Why is the date moving?',
  reasonPlaceholder: 'e.g. the build-out slipped; the administrator will confirm next week',
  askAgainWhenDateKnown:
    'We will ask on the day it is due to open, and every day after, until somebody says it opened.',
  askAgainWhenDateUnknown:
    'We will ask again tomorrow, and every day, until somebody says it opened.',
  savedWhenDateKnown: 'Start date updated',
  savedWhenDateUnknown: 'Recorded — the date is not known yet',
} as const;

/**
 * What an untouched form holds.
 *
 * ⛔ THE REASON NEVER SURVIVES A SAVE. Found on 2026-09-22, proving this form
 * on a live building: after a successful save the window closed but the card
 * stayed mounted, so reopening it handed the next person the PREVIOUS move's
 * explanation, already in the box, with Save live before they typed anything.
 * A second move would then have recorded the first move's reason — a plausible,
 * false line in a building's history, which is worse than no line at all and is
 * exactly what the reason requirement exists to prevent.
 */
export function freshStartDateMoveDraft(initialDate: string): StartDateMoveDraft {
  return { date: initialDate, dateUnknown: false, reason: '' };
}

/**
 * May this be saved?
 *
 * ⛔ No reason, no save — the database refuses it anyway, and a round trip that
 * comes back as a red box is a worse way to learn it than a disabled button.
 * ⛔ Whitespace is not an answer (the `trim()` half of TCSD-0234).
 * ⛔ No date and not "unknown" is nothing at all.
 */
export function startDateMoveIsReady(draft: StartDateMoveDraft): boolean {
  if (draft.reason.trim().length === 0) return false;
  if (!draft.dateUnknown && !draft.date) return false;
  return true;
}

/**
 * The arguments for `update_account_start_date`.
 *
 * ⛔ A new planned date and the clear flag together are refused by the database
 * with their own sentence, so they are never both set here: checking the box
 * drops the date, it does not carry it along.
 */
export function startDateMovePayload(draft: StartDateMoveDraft): {
  planned: string | null;
  reason: string;
  clearPlanned: boolean;
} {
  return {
    planned: draft.dateUnknown ? null : draft.date,
    reason: draft.reason.trim(),
    clearPlanned: draft.dateUnknown,
  };
}

export interface StartDateMove {
  draft: StartDateMoveDraft;
  setDate: (v: string) => void;
  setDateUnknown: (v: boolean) => void;
  setReason: (v: string) => void;
  /** Put the form back to untouched — no reason, no "not known yet". */
  reset: (nextDate: string) => void;
  /** Readiness — a caller disables its Save on this, never on its own rule. */
  ready: boolean;
  busy: boolean;
  /** Runs the write. Never throws: the caller renders `error` in its window. */
  submit: () => Promise<{ ok: boolean; error: string | null }>;
}

/**
 * The state, the readiness and the write. One hook, one call site in the app.
 *
 * ⛔ The write is an RPC, so `commitWrite`'s zero-row proof does not apply —
 * the function either raises or it does not, and there is no PostgREST 204 to
 * misread (PLAYBOOK §15 question 1). Question 2 applies in full and is handled
 * in `updateAccountStartDate`, which tells every loader registered against the
 * tables the function writes.
 */
export function useStartDateMove({
  facilityId,
  initialDate,
  onSaved,
}: {
  facilityId: string | null | undefined;
  /** What the date box starts on. ⛔ The caller decides, because the two
   *  callers want different things: the overdue dialog's planned date has
   *  already passed so it seeds today, and the Account start card seeds the
   *  date the building currently holds. */
  initialDate: string;
  /** Called after a write that landed, so the caller can close and refetch. */
  onSaved: () => void;
}): StartDateMove {
  const toast = useToast();
  const [draft, setDraft] = useState<StartDateMoveDraft>(() =>
    freshStartDateMoveDraft(initialDate),
  );
  const [busy, setBusy] = useState(false);

  const setDate = useCallback((v: string) => setDraft((d) => ({ ...d, date: v })), []);
  const setReason = useCallback((v: string) => setDraft((d) => ({ ...d, reason: v })), []);
  const setDateUnknown = useCallback(
    (v: boolean) => setDraft((d) => ({ ...d, dateUnknown: v })),
    [],
  );
  /** Put the form back to untouched. The caller calls this when it opens the
   *  window, so a cancelled draft does not come back either. */
  const reset = useCallback((nextDate: string) => {
    setDraft(freshStartDateMoveDraft(nextDate));
  }, []);

  const ready = useMemo(() => startDateMoveIsReady(draft) && !!facilityId, [draft, facilityId]);

  const submit = useCallback(async () => {
    if (!facilityId) {
      return { ok: false, error: 'This card has no linked building yet, so the start date cannot be changed here.' };
    }
    if (!startDateMoveIsReady(draft)) {
      return { ok: false, error: 'Say why the start date is moving before saving.' };
    }
    setBusy(true);
    try {
      const saved = startDateMovePayload(draft);
      await updateAccountStartDate(facilityId, saved);
      // ⛔ The reason does not survive its own save. See freshStartDateMoveDraft.
      setDraft(freshStartDateMoveDraft(saved.planned ?? ''));
      toast.success(
        draft.dateUnknown
          ? START_DATE_MOVE_COPY.savedWhenDateUnknown
          : START_DATE_MOVE_COPY.savedWhenDateKnown,
      );
      onSaved();
      return { ok: true, error: null };
    } catch (e) {
      // One unwrapper, one source tag. The panel used to call this without a
      // tag and the dialog with one, so the same refusal was logged two ways.
      return { ok: false, error: errorMessage(e, 'Something went wrong.', 'start-date-move') };
    } finally {
      setBusy(false);
    }
  }, [draft, facilityId, onSaved, toast]);

  return { draft, setDate, setDateUnknown, setReason, reset, ready, busy, submit };
}

const INPUT_CLASS =
  'mt-1 w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-sm text-tcs-text focus:outline-none focus:ring-2 focus:ring-tcs-accent';

/**
 * The fields. The caller renders its own Cancel/Save — the overdue dialog has
 * its own footer and the Account start card has the window's pinned one, and a
 * second Save inside the body is the thing the window pattern exists to avoid
 * (TCSD-0247/0248/0249, PLAYBOOK §15b).
 */
export function StartDateMoveFields({ move }: { move: StartDateMove }) {
  const copy = START_DATE_MOVE_COPY;
  const { draft, setDate, setDateUnknown, setReason, busy } = move;
  return (
    <div className="space-y-3">
      {/* ⛔ "WE DON'T KNOW YET" IS A REAL ANSWER, and the ask keeps coming. A
          building with no start date at all is named in the daily ask every
          day rather than skipped silently. */}
      <label className="flex items-center gap-2 text-sm text-tcs-text">
        <input
          type="checkbox"
          data-testid="start-date-unknown"
          checked={draft.dateUnknown}
          onChange={(e) => setDateUnknown(e.target.checked)}
          disabled={busy}
        />
        {copy.unknownLabel}
      </label>

      {!draft.dateUnknown && (
        <label className="block">
          <span className="text-xs font-medium text-tcs-text-secondary">{copy.dateLabel}</span>
          <input
            type="date"
            data-testid="start-date-new-planned"
            value={draft.date}
            onChange={(e) => setDate(e.target.value)}
            disabled={busy}
            className={INPUT_CLASS}
          />
        </label>
      )}

      {/* ⛔ WHY IT SLIPPED IS THE POINT. It is recorded, not approved — nobody
          has to say yes. It lands in the building's own lifecycle note, which
          is the only place the history of a slip lives. */}
      <label className="block">
        <span className="text-xs font-medium text-tcs-text-secondary">{copy.reasonLabel}</span>
        <input
          type="text"
          data-testid="start-date-reason"
          value={draft.reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={copy.reasonPlaceholder}
          disabled={busy}
          className={INPUT_CLASS}
        />
      </label>

      <p className="text-[11px] text-tcs-text-secondary">
        {draft.dateUnknown ? copy.askAgainWhenDateUnknown : copy.askAgainWhenDateKnown}
      </p>
    </div>
  );
}
